import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnChanges,
  Output,
  SimpleChanges,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HapticsService } from '@core';
import { SzButtonComponent, SzIconComponent, SzNumFocalComponent, SzNumpadGridComponent, SzNumpadKey } from '@ui';
import { RunInput } from '../../../application/state';
import { centsDigitsToSeconds, formatCentsDigits, MAX_TIME_DIGITS } from '../time-digits.util';

@Component({
  selector: 'lib-numpad-tpr',
  templateUrl: './numpad-tpr.component.html',
  styleUrls: ['./numpad-tpr.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, SzNumpadGridComponent, SzNumFocalComponent, SzButtonComponent, SzIconComponent]
})
export class NumpadTprComponent implements OnChanges {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly haptics: HapticsService = inject(HapticsService);

  readonly initialPayload: InputSignal<RunInput | null> = input<RunInput | null>(null);
  readonly resetTrigger: InputSignal<number> = input<number>(0);

  @Output() readonly submitRun: EventEmitter<RunInput> = new EventEmitter<RunInput>();
  @Output() readonly cancel: EventEmitter<void> = new EventEmitter<void>();

  readonly digits: WritableSignal<string> = signal<string>('');

  readonly displayValue: Signal<string> = computed(() => formatCentsDigits(this.digits()));

  readonly canSubmit: Signal<boolean> = computed(() => centsDigitsToSeconds(this.digits()) > 0);

  readonly keys: Signal<readonly SzNumpadKey[]> = computed((): readonly SzNumpadKey[] => [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
    { label: '00', value: '00', variant: 'fn' },
    { label: '0', value: '0' },
    { label: this.translate.instant('session.numpad.backspace'), value: 'del', variant: 'fn', icon: 'arrow-back' }
  ]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialPayload'] || changes['resetTrigger']) {
      const init: number | null = this.initialPayload()?.timeSeconds ?? null;
      this.digits.set(init === null ? '' : Math.round(init * 100).toString());
    }
  }

  onKeyPress(value: string): void {
    if (value === 'del') {
      this.digits.update((cur: string) => cur.slice(0, -1));
      return;
    }
    if (value === '00') {
      this.digits.update((cur: string) =>
        cur.length + 2 > MAX_TIME_DIGITS ? cur + '0'.repeat(Math.max(0, MAX_TIME_DIGITS - cur.length)) : cur + '00'
      );
      return;
    }
    this.digits.update((cur: string) => (cur.length >= MAX_TIME_DIGITS ? cur : cur + value));
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.haptics.impact('medium');
    this.submitRun.emit({ timeSeconds: centsDigitsToSeconds(this.digits()) });
  }
}
